import React, { useState, useEffect, useRef } from 'react';
import Navbar from '../../components/Navbar';
import ContactStrip from '../../components/ContactStrip';
import { useLang } from '../../context/LanguageContext';
import { getVideos, getHeroProfile } from '../../services/api';
import styles from './ActorPage.module.css';

const FALLBACK_HEADSHOTS = [
  '/images/headshot1.jpg',
  '/images/headshot2.jpg',
  '/images/headshot3.jpg',
  '/images/headshot4.jpg',
  '/images/headshot5.jpg',
];

const FALLBACK_FILM = [
  { title: 'Black Panther: Wakanda Forever', role: 'Supporting', detail: 'Dir. Ryan Coogler' },
  { title: 'Monitor',                        role: 'Supporting', detail: 'Dir. Matt Black & Ryan Polly' },
  { title: 'White Elephant',                 role: 'Supporting', detail: 'Dir. Jesse V. Johnson' },
  { title: 'Father of the Bride',            role: 'Supporting', detail: 'Dir. Gary Alazraki' },
];
const FALLBACK_TV = [
  { title: 'Black Mafia Family', role: 'Co-Star',    detail: 'Starz' },
  { title: 'Found',              role: 'Co-Star',    detail: 'NBC' },
  { title: 'Superstition',       role: 'Guest Star', detail: 'Syfy' },
  { title: 'Raising Dion',       role: 'Co-Star',    detail: 'Netflix' },
  { title: 'NCIS: New Orleans',  role: 'Co-Star',    detail: 'CBS' },
];
const FALLBACK_THEATRE = [
  { title: 'Venus in Fur',  role: 'Lead',       detail: 'Theatre UCF Thesis' },
  { title: 'Autobahn',      role: 'Supporting', detail: 'Orlando Fringe' },
  { title: 'Trevor',        role: 'Supporting', detail: 'Theatre UCF' },
  { title: 'Shipwrecked!',  role: 'Supporting', detail: 'Theatre UCF' },
];
const FALLBACK_COMMERCIAL = [
  { title: 'Credit Karma',     role: 'Principal',     detail: 'The Cross Agency' },
  { title: "Hendrick's Spain", role: 'VO Commercial', detail: 'All Ages Production' },
];
const FALLBACK_TRAINING = [
  { title: 'Character Development', coach: 'Peter Jay Fernandez',    detail: 'Online · Active' },
  { title: 'On-Camera',             coach: 'James Calleri',          detail: 'Online · 2021–2022' },
  { title: 'Character Work',        coach: 'Adam Stoner',            detail: 'ATL, GA · 2 yrs' },
  { title: 'Auditioning',           coach: 'Martinez',               detail: 'Get Scene · 1 year' },
  { title: 'Improv & Sketch',       coach: 'Quadruple-stuffed-Oreos',detail: 'Theatre UCF · 2 yrs' },
  { title: 'BFA Acting',            coach: 'Mark Routhier',          detail: 'Theatre UCF' },
];
const FALLBACK_SKILLS = [
  { text_en: 'Firearms Training (Handguns & Semi-automatics)', text_es: 'Entrenamiento con armas de fuego (pistolas y semiautomáticas)' },
  { text_en: 'Web / App Developer',  text_es: 'Desarrollador web / de aplicaciones' },
  { text_en: 'Bartender',            text_es: 'Bartender' },
  { text_en: 'Poker Player',         text_es: 'Jugador de póker' },
  { text_en: 'Competitive Gamer',    text_es: 'Gamer competitivo' },
];
const FALLBACK_DIALECTS = [
  { text_en: 'Mexican',    text_es: 'Mexicano' },
  { text_en: 'Colombian',  text_es: 'Colombiano' },
  { text_en: 'Argentinian',text_es: 'Argentino' },
  { text_en: 'Cuban',      text_es: 'Cubano' },
  { text_en: 'Brazilian',  text_es: 'Brasileño' },
  { text_en: 'Peruvian',   text_es: 'Peruano' },
  { text_en: 'Italian',    text_es: 'Italiano' },
  { text_en: 'British',    text_es: 'Británico' },
  { text_en: 'Cockney',    text_es: 'Cockney' },
  { text_en: 'Scottish',   text_es: 'Escocés' },
  { text_en: 'Russian',    text_es: 'Ruso' },
  { text_en: 'German',     text_es: 'Alemán' },
];

// ── SMART SCROLL HOOK ──────────────────────────────────────────
function useSmartCascade(ref) {
  useEffect(() => {
    if (!ref.current) return;
    const VELOCITY_THRESHOLD = 800;
    const FAST_STAGGER = 60;
    const elements = Array.from(ref.current.querySelectorAll('[data-cascade]'));
    const revealed = new Set();

    const reveal = (el) => {
      if (revealed.has(el)) return;
      revealed.add(el);
      el.classList.add(styles.cascadeVisible);
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => { if (entry.isIntersecting) reveal(entry.target); });
    }, { threshold: 0.15 });

    elements.forEach(el => observer.observe(el));

    let lastY = window.scrollY;
    let lastTime = performance.now();
    let fastTimer = null;

    const onScroll = () => {
      const now = performance.now();
      const dy = Math.abs(window.scrollY - lastY);
      const dt = now - lastTime;
      const velocity = (dy / dt) * 1000;
      lastY = window.scrollY;
      lastTime = now;

      if (velocity > VELOCITY_THRESHOLD) {
        clearTimeout(fastTimer);
        fastTimer = setTimeout(() => {
          const viewportBottom = window.scrollY + window.innerHeight;
          const skipped = elements.filter(el => {
            if (revealed.has(el)) return false;
            const top = el.getBoundingClientRect().top + window.scrollY;
            return top < viewportBottom;
          });
          skipped.forEach((el, i) => setTimeout(() => reveal(el), i * FAST_STAGGER));
        }, 150);
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
      clearTimeout(fastTimer);
    };
  }, [ref]);
}

// ── CREDIT TABLE ───────────────────────────────────────────────
function CreditTable({ data, activeCredit, setActiveCredit, offset = 0 }) {
  return (
    <div data-cascade className={`${styles.credits} ${styles.cascade}`}>
      {data.map((c, i) => (
        <div key={i}
          className={`${styles.creditRow} ${activeCredit === offset + i ? styles.creditActive : ''}`}
          onMouseEnter={() => setActiveCredit(offset + i)}
          onMouseLeave={() => setActiveCredit(null)}
        >
          <span className={styles.creditTitle}>{c.title}</span>
          <span className={styles.creditRole}>{c.role}</span>
          <span className={styles.creditType}>{c.detail}</span>
        </div>
      ))}
    </div>
  );
}

// ── MEDIA PANEL (headshots only) ───────────────────────────────
function MediaPanel() {
  const cached = localStorage.getItem('jm_headshots');
  const [headshots, setHeadshots] = useState(cached ? JSON.parse(cached) : FALLBACK_HEADSHOTS);
  const [shimmer, setShimmer] = useState(!cached);
  const [current, setCurrent] = useState(0);
  const intervalRef = useRef(null);
  const userPausedRef = useRef(false);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL || ''}/api/photos`)
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data) && data.length) {
          const urls = data.map(p => p.url);
          localStorage.setItem('jm_headshots', JSON.stringify(urls));
          setHeadshots(urls);
        } else {
          setHeadshots(FALLBACK_HEADSHOTS);
        }
        setShimmer(false);
      })
      .catch(() => {
        setHeadshots(FALLBACK_HEADSHOTS);
        setShimmer(false);
      });
  }, []);

  const startAutoplay = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCurrent(prev => (prev + 1) % headshots.length);
    }, 4000);
  };
  const stopAutoplay = () => clearInterval(intervalRef.current);

  useEffect(() => {
    if (!userPausedRef.current) startAutoplay();
    return stopAutoplay;
  }, [headshots]);

  const prev = () => { userPausedRef.current = true; stopAutoplay(); setCurrent(p => (p - 1 + headshots.length) % headshots.length); };
  const next = () => { userPausedRef.current = true; stopAutoplay(); setCurrent(p => (p + 1) % headshots.length); };

  return (
    <div className={styles.mediaPanel}>
      <div className={`${styles.carousel} ${shimmer ? styles.carouselShimmer : ''}`}>
        <button className={`${styles.arrow} ${styles.arrowLeft}`} onClick={prev} aria-label="Previous">‹</button>
        <div className={styles.carouselTrack}>
          {headshots.map((src, i) => (
            <img key={src} src={src} alt={`Joaquin Montes headshot ${i + 1}`}
              className={`${styles.carouselImg} ${i === current ? styles.carouselImgActive : ''}`} />
          ))}
        </div>
        <button className={`${styles.arrow} ${styles.arrowRight}`} onClick={next} aria-label="Next">›</button>
        <div className={styles.carouselDots}>
          {headshots.map((_, i) => (
            <button key={i} className={`${styles.dot} ${i === current ? styles.dotActive : ''}`}
              onClick={() => { stopAutoplay(); userPausedRef.current = true; setCurrent(i); }} aria-label={`Photo ${i + 1}`} />
          ))}
        </div>
        <div className={styles.carouselCounter}>{current + 1} / {headshots.length}</div>
      </div>
    </div>
  );
}

// ── REELS SECTION ──────────────────────────────────────────────
function ReelsSection() {
  const { t } = useLang();
  const [videos, setVideos] = useState([]);
  const [activeIdx, setActiveIdx] = useState(0);
  const playerRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    getVideos()
      .then(data => { if (Array.isArray(data) && data.length) setVideos(data); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!listRef.current) return;
    const active = listRef.current.children[activeIdx];
    if (active) active.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [activeIdx]);

  if (videos.length === 0) return null;

  const activeVid = videos[activeIdx];

  const handleSelect = (idx) => {
    setActiveIdx(idx);
    if (playerRef.current) {
      playerRef.current.load();
      playerRef.current.play().catch(() => {});
    }
  };

  return (
    <section className={styles.reelsSection}>
      <h2 id="reels" className={styles.sectionTitle}>{t('actor_demo_reel')}</h2>
      <div className={styles.reelsLayout}>
        <div className={styles.reelPlayerCol}>
          <div className={styles.reelPlayer}>
            <video
              ref={playerRef}
              key={activeVid._id}
              src={activeVid.url}
              poster={activeVid.thumbUrl || undefined}
              className={styles.reelVideo}
              controls
              playsInline
            />
          </div>
          {(activeVid.title || activeVid.credit || activeVid.director || activeVid.studio) && (
            <div className={styles.reelDetails}>
              {activeVid.title    && <span className={styles.reelDetailTitle}>{activeVid.title}</span>}
              {activeVid.credit   && <span className={styles.reelDetailCredit}>{activeVid.credit}</span>}
              {activeVid.director && <span className={styles.reelDetailDirector}>Dir. {activeVid.director}</span>}
              {activeVid.studio   && <span className={styles.reelDetailStudio}>{activeVid.studio}</span>}
            </div>
          )}
        </div>
        {videos.length > 1 && (
          <div className={styles.reelVideoList} ref={listRef}>
            {videos.map((v, i) => (
              <button
                key={v._id}
                className={`${styles.reelVideoRow} ${i === activeIdx ? styles.reelVideoRowActive : ''}`}
                onClick={() => handleSelect(i)}
              >
                {v.thumbUrl
                  ? <img src={v.thumbUrl} alt={v.title || ''} className={styles.reelVideoThumb} />
                  : <div className={styles.reelVideoThumbBlank} />
                }
                <div className={styles.reelVideoMeta}>
                  {v.credit && <span className={styles.reelVideoCredit}>{v.credit}</span>}
                  <span className={styles.reelVideoTitle}>{v.title || '—'}</span>
                  {v.director && <span className={styles.reelVideoSub}>Dir. {v.director}</span>}
                  {v.studio   && <span className={styles.reelVideoSub}>{v.studio}</span>}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

// ── SCROLL INDICATOR ──────────────────────────────────────────
function ScrollIndicator({ styles }) {
  const [hidden, setHidden] = React.useState(false);
  React.useEffect(() => {
    const onScroll = () => setHidden(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  const handleClick = () => window.scrollBy({ top: window.innerHeight * 0.6, behavior: 'smooth' });
  return (
    <div className={`${styles.scrollIndicator} ${hidden ? styles.hidden : ''}`} onClick={handleClick}>
      <span className={styles.scrollGlyph}>↓</span>
      <span className={styles.scrollLabel}>scroll</span>
    </div>
  );
}

// ── PAGE ───────────────────────────────────────────────────────
export default function ActorPage() {
  const [activeCredit, setActiveCredit] = useState(null);
  const { t, lang } = useLang();
  const cascadeRef = useRef(null);
  useSmartCascade(cascadeRef);

  const [film, setFilm]           = useState(FALLBACK_FILM);
  const [tv, setTv]               = useState(FALLBACK_TV);
  const [theatre, setTheatre]     = useState(FALLBACK_THEATRE);
  const [commercial, setCommercial] = useState(FALLBACK_COMMERCIAL);
  const [training, setTraining]   = useState(FALLBACK_TRAINING);
  const [skills, setSkills]       = useState(FALLBACK_SKILLS);
  const [dialects, setDialects]   = useState(FALLBACK_DIALECTS);
  const [heroData, setHeroData]   = useState({});

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL || ''}/api/resume`)
      .then(r => r.json())
      .then(data => {
        if (!data || typeof data !== 'object') return;
        if (Array.isArray(data.film)       && data.film.length)       setFilm(data.film);
        if (Array.isArray(data.tv)         && data.tv.length)         setTv(data.tv);
        if (Array.isArray(data.theatre)    && data.theatre.length)    setTheatre(data.theatre);
        if (Array.isArray(data.commercial) && data.commercial.length) setCommercial(data.commercial);
        if (Array.isArray(data.training)   && data.training.length)   setTraining(data.training);
        if (Array.isArray(data.skill)      && data.skill.length)      setSkills(data.skill);
        if (Array.isArray(data.dialect)    && data.dialect.length)    setDialects(data.dialect);
      })
      .catch(() => {});
    getHeroProfile().then(setHeroData).catch(() => {});
  }, []);

  return (
    <>
      <Navbar
        theme="light"
        wordmark="Joaquin Montes"
        navLinks={[
          { key: 'actor_nav_reel',   href: '#reels' },
          { key: 'actor_nav_resume', href: '#resume' },
        ]}
      />
      <div className={styles.root}>
      <div className={styles.grain} />

      {/* HERO */}
      <section className={styles.hero}>
        <div className={styles.heroLeft}>
          <p className={styles.eyebrow}>{heroData.eyebrow || t('actor_eyebrow')}</p>
          <h1 className={styles.name}><em>{heroData.firstName || 'Joaquin'}</em><br />{heroData.lastName || 'Montes'}</h1>
          <p className={styles.tagline}>{heroData.tagline || t('actor_tagline')}</p>
          <div className={styles.heroStats}>
            <div className={styles.stat}>
              <span style={{whiteSpace:'nowrap'}}>{heroData.stat1Val || t('actor_height_val')}</span>
              <p>{heroData.stat1Label || t('actor_height')}</p>
            </div>
            <div className={styles.stat}>
              <span>{heroData.stat2Val || '10'}</span>
              <p>{heroData.stat2Label || t('actor_yrs_pro')}</p>
            </div>
            <div className={styles.stat}>
              <span>{heroData.stat3Val || t('actor_dialect_expert')}</span>
              <p>{heroData.stat3Label || t('actor_dialect_expert_sub')}</p>
            </div>
          </div>
          <div className={styles.bfaBadge}>
            <span className={styles.bfaIcon}>🎓</span>
            <div className={styles.bfaText}>
              <span>{heroData.bfaLine1 || t('actor_bfa_line1')}</span>
              <span className={styles.bfaLine2}>{heroData.bfaLine2 || t('actor_bfa_line2')}</span>
            </div>
          </div>
          <div className={styles.agency}>
            <span className={styles.agencyLabel}>{t('representation')}</span>
            <span className={styles.agencyName}>{heroData.agency || 'Marta Rubio Agency'}</span>
          </div>
        </div>
        <MediaPanel />
        <ScrollIndicator styles={styles} />
      </section>

      {/* QUOTE */}
      <section className={styles.statement}>
        <p>{t('actor_statement')}</p>
        <cite className={styles.statementCite}>— Anton Chekhov</cite>
      </section>

      {/* REELS */}
      <ReelsSection />

      {/* RESUME — cascade ref wraps everything from here down */}
      <div ref={cascadeRef}>
        <div className={styles.resumeWrapper}>
          <section className={styles.section}>
            <h2 id="resume" data-cascade className={`${styles.sectionTitle} ${styles.cascade} ${styles.cascadeTitle}`}>{t('actor_film')}</h2>
            <CreditTable data={film} activeCredit={activeCredit} setActiveCredit={setActiveCredit} offset={0} />
          </section>
          <section className={styles.section} style={{ paddingTop: 0 }}>
            <h2 data-cascade className={`${styles.sectionTitle} ${styles.cascade} ${styles.cascadeTitle}`}>{t('actor_tv')}</h2>
            <CreditTable data={tv} activeCredit={activeCredit} setActiveCredit={setActiveCredit} offset={10} />
          </section>
          <section className={styles.section} style={{ paddingTop: 0 }}>
            <h2 data-cascade className={`${styles.sectionTitle} ${styles.cascade} ${styles.cascadeTitle}`}>{t('actor_theatre')}</h2>
            <CreditTable data={theatre} activeCredit={activeCredit} setActiveCredit={setActiveCredit} offset={20} />
          </section>
          <section className={styles.section} style={{ paddingTop: 0 }}>
            <h2 data-cascade className={`${styles.sectionTitle} ${styles.cascade} ${styles.cascadeTitle}`}>{t('actor_commercial')}</h2>
            <CreditTable data={commercial} activeCredit={activeCredit} setActiveCredit={setActiveCredit} offset={30} />
          </section>
        </div>

        <div className={styles.wideWrapper}>
          <section className={styles.section}>
            <h2 data-cascade className={`${styles.sectionTitle} ${styles.cascade} ${styles.cascadeTitle}`}>{t('actor_training')}</h2>
            <div data-cascade className={`${styles.trainingGrid} ${styles.cascade}`}>
              {training.map((tr, i) => (
                <div key={tr._id || i} className={styles.trainingCard}>
                  <p className={styles.trainingTitle}>{tr.title}</p>
                  <p className={styles.trainingCoach}>{tr.coach}</p>
                  <p className={styles.trainingDetail}>{tr.detail}</p>
                </div>
              ))}
            </div>
          </section>

          <section className={styles.section} style={{ paddingTop: 0 }}>
            <h2 data-cascade className={`${styles.sectionTitle} ${styles.cascade} ${styles.cascadeTitle}`}>{t('actor_special_skills')}</h2>
            <div data-cascade className={`${styles.skills} ${styles.cascade}`}>
              {skills.map((s, i) => (
                <span key={s._id || i} className={styles.skill}>{lang === 'es' ? s.text_es : s.text_en}</span>
              ))}
            </div>
            <h3 data-cascade className={`${styles.subSectionTitle} ${styles.cascade} ${styles.cascadeTitle}`}>{t('actor_dialects')}</h3>
            <div data-cascade className={`${styles.skills} ${styles.cascade}`}>
              {dialects.map((d, i) => (
                <span key={d._id || i} className={styles.skill}>{lang === 'es' ? d.text_es : d.text_en}</span>
              ))}
            </div>
          </section>
        </div>
      </div>

      <ContactStrip theme="light" emailParts={["Joaquinmontesfilm", "@", "gmail.com"]} />
    </div>
    </>
  );
}
